import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUsers, faChartLine, faCog } from '@fortawesome/free-solid-svg-icons';
import { getAllWheels } from '../../services/adminApi.ts';
import { Wheel } from '../../services/adminApi.ts';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #2d3748;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
`;

const WheelsContainer = styled.div`
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const WheelsTitle = styled.h2`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const WheelsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const WheelCard = styled.div`
  background-color: #f7fafc;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const WheelName = styled.h3`
  font-size: 1rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
`;

const WheelDetail = styled.p`
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.25rem;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  background-color: #4299e1;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  text-decoration: none;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: #3182ce;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const getWheelStatusDisplayName = (status: number): string => {
  switch (status) {
    case 0:
      return 'Active';
    case 1:
      return 'Inactive';
    case 2:
      return 'Completed';
    case 3:
      return 'Expired';
    default:
      return 'Unknown';
  }
};

const BusinessDashboard: React.FC = () => {
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [businessUser, setBusinessUser] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get business user data from localStorage
        const userDataString = localStorage.getItem('admin_user');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          setBusinessUser(userData);
        }

        // Fetch wheels
        const wheelsData = await getAllWheels();
        setWheels(wheelsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Business Dashboard</Title>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatTitle>Total Wheels</StatTitle>
          <StatValue>{wheels.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Active Wheels</StatTitle>
          <StatValue>{wheels.filter(wheel => wheel.status === 0).length}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Participants</StatTitle>
          <StatValue>
            {wheels.reduce((total, wheel) => total + wheel.activeUsers.length, 0)}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Matches</StatTitle>
          <StatValue>{wheels.reduce((total, wheel) => total + wheel.matches.length, 0)}</StatValue>
        </StatCard>
      </StatsContainer>

      <WheelsContainer>
        <WheelsTitle>Your Wheels</WheelsTitle>
        {wheels.length === 0 ? (
          <p>No wheels found. Create your first wheel to get started!</p>
        ) : (
          <WheelsList>
            {wheels.map(wheel => (
              <WheelCard key={wheel.id}>
                <WheelName>{wheel.name}</WheelName>
                <WheelDetail>Status: {getWheelStatusDisplayName(wheel.status)}</WheelDetail>
                <WheelDetail>Slots: {wheel.slotCount}</WheelDetail>
                <WheelDetail>Created: {new Date(wheel.createdAt).toLocaleDateString()}</WheelDetail>
                <ActionButton to={`/admin/wheels/${wheel.id}`}>View Details</ActionButton>
              </WheelCard>
            ))}
          </WheelsList>
        )}
      </WheelsContainer>
    </Container>
  );
};

export default BusinessDashboard; 